import { create } from 'zustand';
import type { User } from '@/types/auth';
import { STORAGE_KEYS } from '@/utils/constants';

// Restore auth synchronously on load to avoid race conditions
function getInitialAuthState(): { user: User | null; token: string | null; isAuthenticated: boolean } {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES);
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);

    if (!token || !userStr) {
      return { user: null, token: null, isAuthenticated: false };
    }

    // Check if token is expired
    if (expiresAt && new Date(expiresAt) < new Date()) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES);
      localStorage.removeItem(STORAGE_KEYS.USER);
      return { user: null, token: null, isAuthenticated: false };
    }

    const user = JSON.parse(userStr) as User;
    return { user, token, isAuthenticated: true };
  } catch {
    return { user: null, token: null, isAuthenticated: false };
  }
}

const initialAuth = getInitialAuthState();
let activeConversationSessionId: string | null = null;

function createConversationSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: User, expiresAt: string) => void;
  clearAuth: () => void;
  restoreAuth: () => boolean;
  getSessionId: () => string;
  resetSessionId: () => string;
  getUserDisplayName: () => string;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: initialAuth.user,
  token: initialAuth.token,
  isAuthenticated: initialAuth.isAuthenticated,

  setAuth: (token, user, expiresAt) => {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES, expiresAt);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    activeConversationSessionId = null;
    set({ user, token, isAuthenticated: true });
  },

  clearAuth: () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);  // BUG FIX: Limpiar session_id al logout
    activeConversationSessionId = null;
    set({ user: null, token: null, isAuthenticated: false });
  },

  restoreAuth: () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES);
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);

    if (!token || !userStr) return false;

    if (expiresAt && new Date(expiresAt) < new Date()) {
      get().clearAuth();
      return false;
    }

    try {
      const user = JSON.parse(userStr) as User;
      set({ user, token, isAuthenticated: true });
      return true;
    } catch {
      get().clearAuth();
      return false;
    }
  },

  getSessionId: () => {
    const user = get().user;
    if (!user) return '';
    if (!activeConversationSessionId) {
      activeConversationSessionId = createConversationSessionId();
    }
    return activeConversationSessionId;
  },

  resetSessionId: () => {
    activeConversationSessionId = createConversationSessionId();
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    return activeConversationSessionId;
  },

  getUserDisplayName: () => {
    const user = get().user;
    if (!user) return 'Usuario';
    return user.full_name || user.name || user.display_name || user.username || user.email || 'Usuario';
  },
}));
